import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  ModalBody,
  ModalFooter,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { IoChevronBackOutline } from "react-icons/io5";
import { MdOutlineDelete } from "react-icons/md";
import { Select } from "chakra-react-select"
import { toast } from "react-toastify";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import SimpleTokenFields from "./SimpleTokenFields";
import useNewAuthCreation, { authTypeEnum } from "./useNewAuthCreation";
import useQueryParams from "./useQueryParams";

export default function AuthorizationBodyForm({
  tool,
  stepHook,
  toolUserForm,
  externalAuthHook,
}) {
  const baseUrl = getHostDomain();
  const { register, reset, watch, formState, handleSubmit, setValue } =
    toolUserForm;
  const authForm = watch();

  const { isOpen: isKeyFieldsOpen, onToggle: toggleKeyFields } =
    useDisclosure();
  const {
    getNewAuthFunction,
    redirect_uri,
    makeValuesForQueryAccordingToAgency,
  } = useNewAuthCreation({
    tool,
    toggleKeyFields,
  });
  const agency = useContext(AgencyContext);

  const query = useQueryParams();
  const code = query.get("code");
  const onClickNewAuth = getNewAuthFunction();
  const { linkToolUsers: auths, status, getLinkToolUsers: refreshAuths } = externalAuthHook;

  // Calendar Pool tool — load available pools for the dropdown
  const isCalendarPool = tool?.tool_provider === "calendar_pool";
  const [pools, setPools] = useState([]);
  useEffect(() => {
    if (!isCalendarPool) return;
    fetchController(`${baseUrl}/api/v1/calendar_pool?size=100`, "GET")
      .then((res) => setPools(res?.data?.items || []))
      .catch(() => setPools([]));
  }, [isCalendarPool, baseUrl]);
  const onHandleSubmit = async (data) => {
    const { body, method, url } = makeRequestForAuth({
      data,
      tool,
      query,
      redirect_uri,
    });
    try {
      const respone = await fetchController(baseUrl + url, method, body);
      setValue("tool_user_id", data.tool_user_id || respone.data.id);
      stepHook.goToNext();
      refreshAuths();
    } catch (error) {
      toast.error("Authentication failed, please try again");
    }
  };
  const [selectedAuthStatus, setSelectedAuthStatus] = useState("idle");
  const onDeleteAuth = async () => {
    try {
      setSelectedAuthStatus("deleting");
      await fetchController(
        baseUrl + `/api/v1/tool/ToolUser/${authForm.tool_user_id}`,
        "delete"
      );
      refreshAuths();
      setValue("tool_user_id", undefined);
      toast.success("User deleted");
    } catch (error) {
      toast.error("Some error occured");
    } finally {
      setSelectedAuthStatus("idle");
    }
  };
  const toolUserOptions = auths.map((auth) => ({
    value: auth.id,
    label: auth.name,
  }));
  const { buttonColorScheme } = agency;
  const getAllQueries = (query) => {
    const queries = {};
    for (const [key, value] of query.entries()) {
      if (value) queries[key] = makeValuesForQueryAccordingToAgency(value);
    }
    return queries;
  };
  const makeRequestForAuth = ({ data, tool, query, redirect_uri = "" }) => {
    const queries = getAllQueries(query);
    const makeOauthBody = () => {
      const body = data.tool_user_id
        ? {
            name: data.name,
          }
        : {
            name: data.name,
            tool_id: tool.id,
            credentials: {
              redirect_uri,
              ...queries,
            },
          };
      return body;
    };
    const makeKeysBody = () => {
      const body = data.tool_user_id
        ? {
            name: data.name,
          }
        : {
            name: data.name,
            credentials: data.credentials || {},
            tool_id: tool.id,
          };
      // Calendar Pool tool: store the selected pool id in attributes
      if (tool?.tool_provider === "calendar_pool" && data.pool_id) {
        body.attributes = { ...(body.attributes || {}), pool_id: data.pool_id };
      }
      return body;
    };
    const authenticationTypeMap = {
      [authTypeEnum.OAUTH]: makeOauthBody,
      [authTypeEnum.SIMPLE_TOKEN]: makeKeysBody,
      [authTypeEnum.OAUTH_KEYS]: makeKeysBody,
    };
    const makeBody =
      authenticationTypeMap[tool?.authentication?.method] || makeKeysBody;
    const body = makeBody();
    return {
      body,
      method: data.tool_user_id ? "PUT" : "POST",
      url: data.tool_user_id
        ? `/api/v1/tool/${data.tool_user_id}/LinkToolUser`
        : `/api/v1/tool/${tool.id}/LinkToolUser`,
    };
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(onHandleSubmit)(e); }}>
      <ModalBody>
        {status === "loading" ? (
          <Flex justifyContent={"center"} alignItems={"center"}>
            <Spinner />
          </Flex>
        ) : (
          <Stack spacing={2}>
            {code || isKeyFieldsOpen ? null : (
              <FormControl isRequired>
                <FormLabel>Exisiting Configuration</FormLabel>
                <Flex justifyContent={"center"} alignItems={"center"} gap={2}>
                  <Box flex={1}>
                    <Select
                      placeholder="Choose one"
                      required
                      value={toolUserOptions.find(
                        (toolUser) => toolUser.value === authForm.tool_user
                      )}
                      options={toolUserOptions}
                      onChange={({ value, label }) => {
                        reset({
                          tool_user_id: value,
                          name: label,
                        });
                      }}
                    />
                  </Box>
                  {authForm.tool_user_id ? (
                    <IconButton
                      isLoading={selectedAuthStatus === "deleting"}
                      icon={<MdOutlineDelete />}
                      colorScheme="red"
                      onClick={onDeleteAuth}
                    />
                  ) : null}
                  {authForm.tool_user_id || code ? null : <Text>OR</Text>}
                  {code || authForm.tool_user_id ? null : (
                    <Button
                      onClick={onClickNewAuth}
                      colorScheme={buttonColorScheme}
                    >
                      Start new
                    </Button>
                  )}
                </Flex>
              </FormControl>
            )}
            {isKeyFieldsOpen ? (
              <Flex justifyContent={"flex-start"} alignItems={"center"}>
                <IconButton
                  size={"sm"}
                  isRound
                  icon={<IoChevronBackOutline />}
                  onClick={() => {
                    toggleKeyFields();
                  }}
                />
              </Flex>
            ) : null}
            {authForm.tool_user_id || code || isKeyFieldsOpen ? (
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  {...register("name", {
                    required: true,
                  })}
                />
              </FormControl>
            ) : null}
            {[authTypeEnum.SIMPLE_TOKEN, authTypeEnum.OAUTH_KEYS].includes(
              tool?.authentication?.method
            ) && isKeyFieldsOpen ? (
              <SimpleTokenFields
                toolUserForm={toolUserForm}
                key_fields={tool?.authentication?.key_fields}
              />
            ) : null}
            {isCalendarPool && isKeyFieldsOpen ? (
              <>
                <FormControl isRequired>
                  <FormLabel>Calendar Pool</FormLabel>
                  <Select
                    placeholder={
                      pools.length === 0
                        ? "No calendar pools yet — create one under Library → Calendar Pools"
                        : "Select a calendar pool"
                    }
                    isDisabled={pools.length === 0}
                    options={pools.map((p) => ({
                      value: p.id,
                      label: `${p.name} (${p.strategy})`,
                    }))}
                    value={
                      pools
                        .map((p) => ({ value: p.id, label: `${p.name} (${p.strategy})` }))
                        .find((opt) => opt.value === authForm.pool_id) || null
                    }
                    onChange={(opt) => setValue("pool_id", opt?.value || null)}
                  />
                </FormControl>
                {Array.isArray(tool?.tool_functions) && tool.tool_functions.length > 0 ? (
                  <Box mt={2} p={3} bg="gray.50" borderRadius="md">
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      Available functions
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {tool.tool_functions
                        .map((fn) =>
                          fn?.details?.openai_spec?.function?.name || fn?.name
                        )
                        .filter(Boolean)
                        .join(", ")}
                    </Text>
                  </Box>
                ) : null}
              </>
            ) : null}
          </Stack>
        )}
      </ModalBody>

      <ModalFooter>
        <ButtonGroup>
          {authForm.tool_user_id ? (
            <Button
              isLoading={formState.isSubmitting}
              type="button"
              colorScheme="blue"
              onClick={handleSubmit(onHandleSubmit)}
            >
              Next
            </Button>
          ) : null}
          {code || isKeyFieldsOpen ? (
            <Button
              isLoading={formState.isSubmitting}
              type="button"
              onClick={handleSubmit(onHandleSubmit)}
            >
              Create
            </Button>
          ) : null}
        </ButtonGroup>
      </ModalFooter>
    </form>
  );
}
